/**
 * The delivery phases for the **Super Admin** track — the single source of
 * truth for every phase label this app shows.
 *
 * When a phase ships, edit this file and nothing else. The sidebar footer reads
 * `currentPhaseLabel()`. Nothing anywhere else should hardcode a phase number.
 *
 * ## Tracks
 *
 * There are two delivery tracks and their numbers collide: Super Admin is
 * SA-0…SA-7 (PROJECT_SPEC.md §8) and Client Admin is CA-0…CA-7
 * (PROJECT_SPEC_CLIENT.md §11). CLAUDE.md requires the prefix everywhere,
 * because a bare "Phase 3" is genuinely ambiguous now.
 *
 * **This file is the SA track only.** The Client Admin app has its own copy at
 * `apps/client/src/shared/constants/phases.js` declaring `TRACK = 'CA'` and the
 * CA phase list. They are deliberately separate rather than shared: the two
 * apps are separate workspaces, and a shared module would have to live in a new
 * package — a folder-structure change CLAUDE.md says to ask about first, for
 * two short lists that change about once a quarter.
 */

/** This app's track. Every label this file produces carries it. */
export const TRACK = 'SA';

/** Every SA phase, in order. Names match PROJECT_SPEC.md section 8 exactly. */
export const PHASES = [
  { track: TRACK, number: 0, name: 'Foundation' },
  { track: TRACK, number: 1, name: 'Auth & core middleware' },
  { track: TRACK, number: 2, name: 'Partner management' },
  { track: TRACK, number: 3, name: 'Client management & mapping' },
  { track: TRACK, number: 4, name: 'Security parameters' },
  { track: TRACK, number: 5, name: 'QR templates' },
  { track: TRACK, number: 6, name: 'Feature management' },
  { track: TRACK, number: 7, name: 'Hardening, testing & deployment' },
];

/**
 * The highest SA phase whose **frontend** work is complete.
 *
 * Bump this only when a phase is genuinely finished in this repo — see
 * FRONTEND_PROGRESS.md, which must agree with it.
 *
 * **7 as of SA-7 (2026-08-16)**, merged to `develop` and `main`. Everything in
 * that phase shipped except deployment and wildcard DNS, which are deferred
 * until there is a server — so the label is accurate about the code even though
 * one deliverable is outstanding.
 *
 * Its only consumer is the sidebar footer — the dashboard's roadmap card, which
 * used to read `upcomingPhases()`, was removed in SA-7.
 */
export const CURRENT_PHASE = 7;

/**
 * Which SA phase delivered each feature area. Keys are the feature folder names
 * under `src/features/`, so a page can look up its own phase by folder.
 *
 * Every one of these has shipped — no placeholder page reads this any more.
 * Kept because it is the only written record of which phase owns which folder,
 * which is what you want when a bug report says "the partners screen".
 */
export const FEATURE_PHASES = {
  auth: 1,
  partnerManagement: 2,
  clientManagement: 3,
  securityParameters: 4,
  qrTemplates: 5,
  featureManagement: 6,
};

/** Display name for a phase number, e.g. 2 -> 'Partner management'. */
export function phaseName(number) {
  return PHASES.find((phase) => phase.number === number)?.name ?? 'Unknown phase';
}

/**
 * The prefixed identifier for a phase number, e.g. 2 -> 'SA-2'.
 *
 * Everything user-facing goes through this. There is deliberately no helper
 * that renders a bare "Phase N" — that is the ambiguity the track prefix
 * exists to remove.
 */
export function phaseLabel(number) {
  return `${TRACK}-${number}`;
}

/** Empty-state badge text, e.g. 'Coming in SA-2'. */
export function comingInPhase(number) {
  return `Coming in ${phaseLabel(number)}`;
}

/** Sidebar footer label, e.g. 'SA-7 — Hardening, testing & deployment'. */
export function currentPhaseLabel() {
  return `${phaseLabel(CURRENT_PHASE)} — ${phaseName(CURRENT_PHASE)}`;
}

/**
 * Feature areas still to be built, in phase order. Empty now that SA-6 has
 * shipped; kept so a future feature area added at SA-8 needs no new helper.
 */
export function upcomingPhases() {
  return PHASES.filter(
    (phase) =>
      phase.number > CURRENT_PHASE &&
      Object.values(FEATURE_PHASES).includes(phase.number),
  );
}
